import React from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { LuBookmarkPlus, LuListPlus } from 'react-icons/lu';
import { Button, confirm } from 'components/common';
import { useUserPreference } from 'helpers/useUserPreference';
import { extractApiError } from 'helpers/apiError';

const PREF_KEY = 'interaction_cc_names';

const sameList = (a = [], b = []) =>
  a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|');

// The same CC people come back topic after topic — let each user keep their own
// list (stored server-side per user) and pull it into the CC field on demand.
// "Use" merges into what is already picked rather than replacing it.
const SavedCcListActions = ({ value, onApply, enabled }) => {
  const { value: saved, loading, saving, save } = useUserPreference(PREF_KEY, { enabled });
  const current = (value || []).map((c) => String(c).toUpperCase());
  const savedList = saved || [];
  const missing = savedList.filter((c) => !current.includes(c));

  const handleUse = () => onApply([...(value || []), ...missing]);

  const handleSave = async () => {
    if (current.length === 0) {
      const ok = await confirm({
        title: 'Clear saved CC list?',
        message: 'CC is empty — saving will clear your saved list.',
        confirmText: 'Clear list',
        type: 'danger',
      });
      if (!ok) return;
    }
    try {
      const result = await save(current);
      toast.success(result?.length ? `Saved CC list (${result.length})` : 'Saved CC list cleared');
    } catch (err) {
      toast.error(extractApiError(err, 'Could not save CC list'));
    }
  };

  if (loading) return null;

  return (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-mt-1.5">
      {savedList.length > 0 && (
        <Button
          size="xs"
          variant="soft"
          leftIcon={LuListPlus}
          onClick={handleUse}
          disabled={missing.length === 0 || saving}
          title={`Saved: ${savedList.join(', ')}`}
        >
          {missing.length === 0 ? 'Saved list applied' : `Use my saved list (${savedList.join(', ')})`}
        </Button>
      )}
      <Button
        size="xs"
        variant="ghost"
        leftIcon={LuBookmarkPlus}
        onClick={handleSave}
        loading={saving}
        disabled={sameList(current, savedList) || (current.length === 0 && savedList.length === 0)}
      >
        Save as my CC list
      </Button>
    </div>
  );
};

SavedCcListActions.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string),
  onApply: PropTypes.func.isRequired,
  enabled: PropTypes.bool,
};

SavedCcListActions.defaultProps = {
  value: [],
  enabled: true,
};

export default SavedCcListActions;
